import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CandleChart as LegacyCandleChart } from '../../../../../ProfitCalculations/Content/SingleProfitCalculation/Result/CandleChart';
import { useResultData } from '../../context';
import { windowForTransaction } from '../../derived/candles';
import { ComponentDimensions } from '../../../../../../types/Layout';
import { CompactTransaction } from '../../../../../ProfitCalculations/types';

interface Props {
  txId: number;
}

const EMPTY_WINDOW = { candles: [], start: 0, end: 0, transactions: [] };

const ListCandleChart = ({ txId }: Props) => {
  const { raw } = useResultData();
  const ref = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<ComponentDimensions | null>(
    null,
  );
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const measure = () =>
      setDimensions({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // Only build the candle window + mount Victory once the row is near the
  // viewport, so a long transaction list does not render dozens of charts
  // up front.
  useEffect(() => {
    if (!ref.current || visible) return;
    const element = ref.current;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          intersectionObserver.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    intersectionObserver.observe(element);
    return () => intersectionObserver.disconnect();
  }, [visible]);

  const candleWindow = useMemo(
    () => (visible ? windowForTransaction(txId, raw) : EMPTY_WINDOW),
    [visible, raw, txId],
  );

  return (
    <div
      ref={ref}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      {visible && dimensions && candleWindow.candles.length > 0 && (
        <div
          className="nc-chart-fade-in"
          style={{ width: '100%', height: '100%' }}
        >
          <LegacyCandleChart
            candles={candleWindow.candles}
            compactTransactions={
              candleWindow.transactions as CompactTransaction[]
            }
            signals={[]}
            rootDimensions={dimensions}
            headless
          />
        </div>
      )}
    </div>
  );
};

export default ListCandleChart;
